import { Component } from 'react'
import { Container, Row, Col, Button } from 'reactstrap'
import { Logistics } from './Logistics'
import { FileDetails } from './FileDetails'
import debounce from 'lodash/debounce'
import { toUsersSelect } from 'components/Users/serializers'
import { ProposalThumbnail } from 'components/proposals/Proposal/common/ProposalThumbnail'
import { getBaseUrl } from 'utils/api'
import { getFileBaseUrl } from 'components/proposals/Proposal/utils'
import s from './Details.scss'
import { Link } from 'react-router-dom'
import cn from 'classnames'
import { fromConstantPropertyToSelectValue } from 'utils/serializers'
import { SendToReviewBtn } from './buttons/SendToReviewBtn'
import { EmailClientBtn } from './buttons/EmailClientBtn'
import { FaEdit, FaCheckSquare, FaTrashAlt, FaCertificate } from 'react-icons/fa'
import swal from 'sweetalert'
import { getUser, isAdmin, getToken } from 'utils/authService'
import { FollowupDateModal } from 'components/common/modals/FollowupDateModal'
import { getNoBidClientStyle } from 'utils/helpers'
import { withSweetAlert } from 'hocs'

const followUpStatuses = ['SENT', 'RECEIVED']

class Details extends Component {
  state = {
    proposal: this.props.proposal,
    requiredFields: ['name'],
    error: false,
    showProposalFollowup: false
  }

  componentDidUpdate(prevProps) {
    const proposalProperties = [
      'version',
      'status',
      'content',
      'contacts',
      'sentForReviewTo',
      'sentForReviewAt'
    ]
    const proposalPropertiesChanged = proposalProperties.some(
      property => this.props.proposal[property] !== prevProps.proposal[property]
    )
    if (this.props.proposalId !== prevProps.proposalId || proposalPropertiesChanged) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        proposal: this.props.proposal
      })
    }
  }

  changeProperty = (value, property) => {
    const proposalValue = this.state.proposal[property]
    if (!proposalValue || proposalValue.toString() !== value) {
      this.setState(
        {
          proposal: {
            ...this.state.proposal,
            [property]: value
          }
        },
        () => {
          if (property === 'followUpDate') {
            this.onSave()
          }
        }
      )
    }
  }

  updateState = (value, property, cb) => {
    const proposalValue = this.state.proposal[property]
    if (!proposalValue || proposalValue.toString() !== value) {
      this.setState({
        ...this.state,
        proposal: {
          ...this.state.proposal,
          [property]: value
        }
      })

      cb && cb(proposalValue)
    }
  }

  changeProperties = partialState => {
    const newProposal = { ...this.state.proposal, ...partialState }
    this.setState({
      ...this.state,
      proposal: newProposal
    })
  }

  changeAssignee = value => {
    const assignee = this.props.assignees.find(assignee => assignee.id === value.value)
    this.changeProperty(assignee, 'assignee')
  }

  changeStatus = value => {
    if (followUpStatuses.includes(value.value)) {
      this.toggleFollowupDateModal()
    }
    this.changeProperty(value, 'status')
  }

  validateRequired = async () => {
    await this.setState({
      error: false
    })
    this.state.requiredFields.forEach(field => {
      if (!this.state.proposal[field]) {
        this.setState({
          error: true
        })
      }
    })
  }

  onSave = debounce(async () => {
    const { proposal } = this.state

    await this.validateRequired()
    if (!this.state.error) {
      this.props.updateProposal(proposal)
    }
  }, 500)

  getAssignee = assignee => {
    if (!assignee?.id) {
      return null
    }
    if (assignee) {
      return {
        value: assignee.id,
        label: `${assignee.firstName} ${assignee.lastName}`
      }
    }
    return ''
  }

  approveProposal = async () => {
    const willApprove = await swal({
      title: 'You are about to Approve this Proposal to be sent out to your Client.',
      text: 'Are you sure you want to continue?',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willApprove) {
      this.props.approveProposal()
    }
  }

  toggleFollowupDateModal = () => {
    this.setState(prevState => ({
      showProposalFollowup: !prevState.showProposalFollowup
    }))
  }

  deleteProposal = async () => {
    const sweetAlertFactory = withSweetAlert(
      <div>
        <p style={{ color: 'red' }}>This action can&#39;t be reverted</p>
        The proposal PDF(s), all associated links, and related tasks will be permanently deleted.
      </div>
    )
    sweetAlertFactory(
      {
        title: `Are you sure you want to delete this proposal?`,
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      () => {
        this.props.deleteProposal()
      },
      { cancelConfirmation: false }
    )
  }

  render() {
    const { proposal, showProposalFollowup } = this.state
    const {
      name,
      value,
      dueDate,
      followUpDate,
      status,
      signed,
      s3ContentKey,
      s3ThumbnailKey,
      assignee,
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
      deal,
      contacts,
      company,
      approvedAt,
      approvedBy,
      sentForReviewAt,
      sentForReviewTo,
      quoteNumber
    } = proposal
    const {
      proposalId,
      assignees,
      dealName,
      toggleScheduleTaskModal,
      toggleSendForInternalReviewModal,
      clientContacts
    } = this.props
    const baseUrl = getBaseUrl('/v1')
    const fileUrl = getFileBaseUrl()
    const thumbnailUrl = s3ThumbnailKey
      ? getBaseUrl(`/v1/proposals/thumbnail/${proposalId}?x-token-tracking=${getToken()}`)
      : ''
    const { id: loggedUserId } = getUser()
    const isProposalReviewer =
      sentForReviewTo.some(user => user.id === loggedUserId) || createdBy === loggedUserId
    const mainContacts = clientContacts.filter(contact => contact.isMain)
    const otherContacts = clientContacts.filter(contact => !contact.isMain)

    return (
      <>
        <Container>
          <header className={s.header}>
            <Link to={`/opportunities/${deal?.id}/proposals`} className={s.greyBtn}>
              Back to All Proposals for this Opportunity
            </Link>
            {(isAdmin() || loggedUserId === createdBy || loggedUserId === deal.dealManager?.id) && (
              <div className={s.deleteIcon} onClick={this.deleteProposal}>
                <FaTrashAlt />
              </div>
            )}
            <div>
              <label>Opportunity: </label>
              <Link to={`/opportunities/${deal?.id}`}> {deal?.name}</Link>
            </div>
            <div>
              <label className={cn(s.pullLeft, s.marginRight10)}>Client: </label>
              <div className={s.pullLeft}>
                {company.id === deal.awardedCompany?.id && (
                  <FaCertificate className={s.awardIcon} title="Opportunity Awarded" />
                )}
                <Link
                  to={`/companies/${company.id}`}
                  className={s.link}
                  style={getNoBidClientStyle(company.noBid)}
                >
                  {company.name}
                </Link>
              </div>
            </div>
            <div className={s.clearfix} />
            {!!mainContacts.length && (
              <div>
                <label className={cn(s.pullLeft, s.marginRight10)}>Client Main Contacts: </label>
                <div className={s.pullLeft}>
                  {mainContacts.map(contact => (
                    <div key={`contact_${contact.id}`}>
                      <Link to={`/contacts/${contact.contact.id}`} className={s.link}>
                        {`${contact.contact.firstName} ${contact.contact.lastName}`}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className={s.clearfix} />
            {!!otherContacts.length && (
              <div>
                <label className={cn(s.pullLeft, s.marginRight10)}>Client Other Contacts: </label>
                <div className={s.pullLeft}>
                  {otherContacts.map(contact => (
                    <div key={`contact_${contact.id}`}>
                      <Link to={`/contacts/${contact.contact.id}`} className={s.link}>
                        {`${contact.contact.firstName} ${contact.contact.lastName}`}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className={s.clearfix} />
            <div className={s.marginTop5}>
              <label>Current Proposal Status: </label>
              <span> {fromConstantPropertyToSelectValue(status, 'ProposalStatus')?.label}</span>
            </div>
            <div>
              <Link to={`/opportunities/${deal?.id}/proposals`} className={s.link}>
                Click here to view Bid Docs and RFI for this proposal
              </Link>
            </div>
          </header>
          <div className={s.buttonsWrapper}>
            <SendToReviewBtn toggleSendForInternalReviewModal={toggleSendForInternalReviewModal} />
            {isProposalReviewer && (
              <>
                <Button
                  className={s.changesButton}
                  onClick={() =>
                    toggleScheduleTaskModal({
                      defaultSubject: `Task - Make Changes to ${name}`,
                      onSaveCb: params => {
                        this.changeProperties({
                          assignee: params.task.assignee,
                          status: { value: 'CHANGES' }
                        })
                      }
                    })
                  }
                >
                  <FaEdit style={{ position: 'relative', bottom: 2, right: 4 }} />
                  Suggest Changes
                </Button>
                <Button className={s.approveButton} onClick={this.approveProposal}>
                  <FaCheckSquare style={{ position: 'relative', bottom: 2, right: 4 }} />
                  Approve Proposal
                </Button>
              </>
            )}
            <EmailClientBtn proposal={proposal} changeProperty={this.changeProperty} />
          </div>
          <Row>
            <Col className={s.column} lg="4" xs="12" sm="6">
              <p className={s.title}>Proposal Logistics</p>
              <Logistics
                name={name}
                value={value}
                quoteNumber={quoteNumber}
                dueDate={dueDate}
                followUpDate={followUpDate}
                status={status}
                assignee={assignee?.value ? assignee : this.getAssignee(assignee)}
                assignees={toUsersSelect(assignees)}
                contacts={contacts}
                signed={signed}
                changeProperty={this.changeProperty}
                updateState={this.updateState}
                changeAssignee={this.changeAssignee}
                changeStatus={this.changeStatus}
                createdBy={toUsersSelect(assignees).find(user => user.value === createdBy)?.label}
                createdAt={createdAt}
                updatedBy={toUsersSelect(assignees).find(user => user.value === updatedBy)?.label}
                updatedAt={updatedAt}
                deal={deal}
                approvedAt={approvedAt}
                approvedBy={approvedBy}
                sentForReviewAt={sentForReviewAt}
                sentForReviewTo={sentForReviewTo}
              />
            </Col>
            <Col className={s.column} lg="4" xs="12" sm="6">
              <p className={s.title}>File Details</p>
              <FileDetails
                proposal={proposal}
                baseUrl={baseUrl}
                fileUrl={fileUrl}
                proposalId={proposalId}
                dealName={dealName}
              />
            </Col>
            <Col className={s.column} lg="4" xs="12" sm="6">
              <a href={`${fileUrl}/${s3ContentKey}`} target="_blank" rel="noopener noreferrer">
                <ProposalThumbnail proposalId={proposalId} thumbnailUrl={thumbnailUrl} />
              </a>
            </Col>
            <Button className={s.saveBtn} onClick={() => this.onSave()}>
              Save
            </Button>
          </Row>
          {this.state.error && <p className={s.error}>All required fields must have values!</p>}
        </Container>
        <FollowupDateModal
          entity={proposal}
          onSave={followUpDate => this.changeProperty(followUpDate, 'followUpDate')}
          showFollowupDateModal={showProposalFollowup}
          toggleFollowupDateModal={this.toggleFollowupDateModal}
          title="Proposal"
        />
      </>
    )
  }
}

export default Details
